import { useEffect, useState, type JSX } from 'react';

import { AttachmentThumb } from '@/components/issue-reporting/admin/AttachmentThumb';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  useDeleteIssueReportMutation,
  useIssueReportQuery,
  useUpdateIssueStatusMutation,
} from '@/hooks/useIssueReportQueries';
import {
  CATEGORY_LABEL,
  SEVERITY_LABEL,
  STATUS_LABEL,
  formatDateTime,
  severityBadgeVariant,
  statusBadgeVariant,
} from '@/lib/issue-reporting/display';
import { toast } from '@/lib/toast';
import {
  IssueStatus,
  type CapturedConsoleError,
  type CapturedNetworkError,
} from '@/types/issue-reports';

const STATUS_OPTIONS = Object.values(IssueStatus);

function isStatus(v: string): v is IssueStatus {
  return (STATUS_OPTIONS as string[]).includes(v);
}

function parseJsonArray(raw: string | null): readonly unknown[] {
  if (raw === null || raw.trim() === '') return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as unknown[]) : [];
  } catch {
    return [];
  }
}

function MetaRow({ label, value }: { readonly label: string; readonly value: string }): JSX.Element {
  return (
    <div className="flex gap-2 text-sm">
      <span className="w-28 shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 break-words font-medium">{value}</span>
    </div>
  );
}

export interface IssueDetailDialogProps {
  readonly reportId: number;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

export function IssueDetailDialog({ reportId, open, onOpenChange }: IssueDetailDialogProps): JSX.Element {
  const detail = useIssueReportQuery(reportId, open);
  const updateStatus = useUpdateIssueStatusMutation();
  const deleteReport = useDeleteIssueReportMutation();

  const [status, setStatus] = useState<IssueStatus>(IssueStatus.New);
  const [notes, setNotes] = useState('');

  // Sync local triage state when the loaded report changes.
  useEffect(() => {
    if (detail.data !== undefined) {
      setStatus(detail.data.status);
      setNotes(detail.data.adminNotes ?? '');
    }
  }, [detail.data]);

  const report = detail.data;
  const consoleErrors = parseJsonArray(report?.consoleErrorsJson ?? null) as readonly CapturedConsoleError[];
  const networkErrors = parseJsonArray(report?.networkErrorsJson ?? null) as readonly CapturedNetworkError[];

  function handleSaveTriage(): void {
    updateStatus.mutate(
      { id: reportId, body: { status, adminNotes: notes } },
      {
        onSuccess: () => toast.success('Issue updated.'),
        onError: (err) => toast.error('Update failed', { description: err.message }),
      },
    );
  }

  function handleDelete(): void {
    deleteReport.mutate(reportId, {
      onSuccess: () => {
        toast.success('Issue deleted.');
        onOpenChange(false);
      },
      onError: (err) => toast.error('Delete failed', { description: err.message }),
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] max-w-2xl overflow-y-auto"
        data-testid="issue-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle>Issue #{String(reportId)}</DialogTitle>
          <DialogDescription>Full report with captured diagnostics.</DialogDescription>
        </DialogHeader>

        {detail.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {detail.isError && (
          <p className="text-sm text-destructive" data-testid="issue-detail-error">
            Failed to load this report.
          </p>
        )}

        {report !== undefined && (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={severityBadgeVariant(report.severity)}>
                {SEVERITY_LABEL[report.severity]}
              </Badge>
              <Badge variant="outline">{CATEGORY_LABEL[report.category]}</Badge>
              <Badge variant={statusBadgeVariant(report.status)}>{STATUS_LABEL[report.status]}</Badge>
            </div>

            <div className="rounded-md border border-border bg-muted/30 p-3">
              <p className="whitespace-pre-wrap text-sm" data-testid="issue-detail-description">
                {report.description}
              </p>
            </div>

            {/* Screenshots */}
            {report.attachments.length > 0 && (
              <div className="space-y-1">
                <Label>Screenshots ({report.attachments.length})</Label>
                <div className="flex flex-wrap gap-2">
                  {report.attachments.map((a) => (
                    <AttachmentThumb
                      key={a.id}
                      reportId={report.id}
                      attachmentId={a.id}
                      alt={a.fileName}
                      className="h-20 w-20 rounded border border-input object-cover"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Metadata */}
            <div className="space-y-1.5">
              <Label>Context</Label>
              <MetaRow label="Reporter" value={report.reportedByName ?? `User #${String(report.reportedByUserId)}`} />
              <MetaRow label="Email" value={report.reportedByEmail ?? '—'} />
              <MetaRow label="Reported" value={formatDateTime(report.createdAt)} />
              <MetaRow label="Route" value={report.routePath ?? '—'} />
              <MetaRow label="URL" value={report.pageUrl ?? '—'} />
              <MetaRow label="Browser" value={report.browser ?? '—'} />
              <MetaRow label="OS" value={report.operatingSystem ?? '—'} />
              <MetaRow
                label="Viewport"
                value={
                  report.viewportWidth !== null && report.viewportHeight !== null
                    ? `${String(report.viewportWidth)} × ${String(report.viewportHeight)}`
                    : '—'
                }
              />
              <MetaRow label="User agent" value={report.userAgent ?? '—'} />
              {report.resolvedAtUtc !== null && (
                <MetaRow label="Resolved" value={formatDateTime(report.resolvedAtUtc)} />
              )}
            </div>

            {/* Console errors */}
            <div className="space-y-1">
              <Label>Recent console errors ({consoleErrors.length})</Label>
              {consoleErrors.length === 0 ? (
                <p className="text-xs text-muted-foreground">None captured.</p>
              ) : (
                <ul className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-border bg-muted/20 p-2 font-mono text-xs">
                  {consoleErrors.map((c, i) => (
                    <li key={i} className="break-words">
                      <span className={c.level === 'error' ? 'text-destructive' : 'text-yellow-600'}>
                        [{c.level}]
                      </span>{' '}
                      <span className="text-muted-foreground">{formatDateTime(c.at)}</span> — {c.message}
                      {c.source !== undefined && <span className="text-muted-foreground"> ({c.source})</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Network errors */}
            <div className="space-y-1">
              <Label>Recent failed requests ({networkErrors.length})</Label>
              {networkErrors.length === 0 ? (
                <p className="text-xs text-muted-foreground">None captured.</p>
              ) : (
                <ul className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-border bg-muted/20 p-2 font-mono text-xs">
                  {networkErrors.map((n, i) => (
                    <li key={i} className="break-words">
                      <span className="font-semibold text-destructive">{String(n.status)}</span>{' '}
                      <span>{n.method}</span> {n.url}
                      {n.durationMs !== undefined && (
                        <span className="text-muted-foreground"> ({String(n.durationMs)}ms)</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Triage */}
            <div className="space-y-3 rounded-md border border-border p-3">
              <Label>Triage</Label>
              <div className="space-y-1">
                <Label htmlFor="triage-status" className="text-xs text-muted-foreground">
                  Status
                </Label>
                <Select
                  value={status}
                  onValueChange={(v) => {
                    if (isStatus(v)) setStatus(v);
                  }}
                  disabled={updateStatus.isPending}
                >
                  <SelectTrigger id="triage-status" data-testid="triage-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s}>
                        {STATUS_LABEL[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="triage-notes" className="text-xs text-muted-foreground">
                  Admin notes
                </Label>
                <Textarea
                  id="triage-notes"
                  value={notes}
                  onChange={(e) => { setNotes(e.target.value); }}
                  rows={3}
                  maxLength={4000}
                  placeholder="Internal triage notes…"
                  disabled={updateStatus.isPending}
                  data-testid="triage-notes-input"
                />
              </div>
              <Button
                type="button"
                onClick={handleSaveTriage}
                disabled={updateStatus.isPending}
                data-testid="triage-save"
              >
                {updateStatus.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </div>
        )}

        <DialogFooter className="justify-between sm:justify-between">
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteReport.isPending || report === undefined}
            data-testid="issue-delete"
          >
            {deleteReport.isPending ? 'Deleting…' : 'Delete'}
          </Button>
          <Button type="button" variant="outline" onClick={() => { onOpenChange(false); }}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
